import { registerListResourceWithStore } from '@/services/GenericListResourceService'
import store from '@/store'
import ScheduleLayout from './ScheduleLayout.vue'
import { getScheduleIndexRoute } from '@/router/sectionRouteUtils'
// Load shifts settings route module for Vuex resource registrations (shiftDefaults, shiftClasses, breaks, roundingRules).
import '@/modules/shifts/routes'

registerListResourceWithStore('teamSchedules', 'schedule/teams', store)

export default {
  name: 'schedule',
  label: 'Schedule',
  path: 'schedule',
  link: 'schedule',
  props: true,
  icon: 'calendar-alt',
  description: 'View and edit shift schedules.',
  component: ScheduleLayout,
  requirePerm: 'manage_schedule',
  requireGetter: 'hasScheduleFeature',
  require: (state, getters) => getters.scheduleEnabled || state.permissions.includes('edit_settings'),
  breadcrumbLeaf: true,
  children: [
    {
      name: 'schedules',
      navIcon: 'CalendarDays',
      description: 'Build and publish shift schedules.',
      label: 'Schedules',
      icon: 'calendar-days',
      path: 'schedules',
      requireGetter: 'scheduleEnabled',
      component: () => import('./Schedules.vue')
        .catch(error => {
          console.error('Failed to route to Schedules', error)
          throw error
        })
    },
    {
      name: 'schedule-review',
      navIcon: 'CalendarCheck',
      description: 'Review scheduled hours against budget, set budgets for specific dates, and approve schedules.',
      label: 'Review',
      icon: 'list-check',
      title: 'Schedule Review',
      path: 'review',
      requirePerm: 'manage_schedule',
      // Budgets alone are reviewed here too: it is where budgets for specific dates are set.
      requireGetter: 'scheduleBudgetsInUse',
      component: () => import('./review/ScheduleReview.vue')
    },
    {
      name: 'schedule-teams',
      navIcon: 'UsersRound',
      description: 'Group workers into teams for scheduling.',
      label: 'Teams',
      icon: 'users',
      title: 'Schedule Teams',
      path: 'teams',
      requireGetter: 'scheduleEnabled',
      component: () => import('./ScheduleTeamList.vue')
        .catch(error => {
          console.error('Failed to route to ScheduleTeamList', error)
          throw error
        })
    },
    {
      name: 'schedule-settings',
      label: 'Settings',
      icon: 'cog',
      title: 'Schedule Settings',
      path: 'settings',
      beforeEnter: to => {
        if (to.name === 'schedule-settings') {
          return { name: 'shift-defaults', params: to.params, query: to.query, hash: to.hash }
        }
        return true
      },
      requirePerm: 'edit_settings',
      component: () => import('@/components/TabPanel.vue'),
      children: [
        {
          name: 'shift-defaults',
          navIcon: 'SlidersHorizontal',
          label: 'Defaults',
          icon: 'sliders',
          title: 'Shift Defaults',
          path: 'defaults',
          component: () => import('@/modules/shifts/ShiftDefaultsForm.vue')
        },
        {
          name: 'schedule-budgets',
          navIcon: 'Scale',
          navLabel: 'Schedule Budget Defaults',
          label: 'Budget Defaults',
          icon: 'scale-balanced',
          title: 'Schedule Budget Defaults',
          path: 'budgets',
          requireFeature: 'schedule_controls',
          // Hidden until budget warnings or approval are turned on in Defaults, which link here.
          requireGetter: 'scheduleBudgetsInUse',
          component: () => import('./settings/ScheduleBudgetsForm.vue')
        },
        {
          name: 'shift-shiftclasses',
          navIcon: 'CalendarClock',
          label: 'Shift Classes',
          icon: 'user-clock',
          path: 'classes/:view?',
          props: true,
          requireGetter: 'scheduleEnabled',
          component: () => import('@/modules/shifts/ShiftClassList.vue')
        },
        {
          name: 'shift-breaks',
          navLabel: 'Shift Breaks',
          navIcon: 'Coffee',
          label: 'Breaks',
          icon: 'mug-hot',
          path: 'breaks/:view?',
          props: true,
          requireGetter: 'scheduleEnabled',
          component: () => import('@/modules/shifts/BreakList.vue')
        },
        {
          name: 'shift-roundingrules',
          navIcon: 'Calculator',
          label: 'Rounding Rules',
          icon: 'circle-nodes',
          path: 'roundingRules',
          requireFeature: 'schedule',
          component: () => import('@/modules/shifts/RoundingRuleList.vue')
        },
        {
          name: 'schedule-settings-index',
          path: '',
          redirect: { name: 'shift-defaults' }
        },
      ]
    },
    {
      name: 'schedule-index',
      path: '',
      nav: false,
      // beforeEnter (unlike redirect) runs after the router has waited for app state to load, so this
      // reads accurate feature/permission state instead of racing it on a hard page load (e.g. new tab).
      beforeEnter: to => ({ name: getScheduleIndexRoute(store.state, store.getters), params: to.params })
    }
  ]
}
